import { Box, Center, Divider, Flex, Icon, IconButton, Progress, Text } from '@chakra-ui/react'
import { ReactElement } from 'react'
import getIconPath from '../../helpers/getFileIcon'

import { CheckCircleFilledIcon, Loading02Icon, Trash04Icon } from '../../assets/icons'

const UploadedFile = ({
  label,
  isLoading,
  isDisabled,
  isDone,
  file,
  onRemove,
  extra,
}: {
  label?: string
  isDone?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  onRemove?: () => void
  file: File | string
  extra?: ReactElement
}) => {
  const isUrl = typeof file === 'string'

  const getFileName = () => {
    if (!file) return ''
    if (isUrl) {
      return file.split('/').pop()?.split('?')[0]
    }
    return file.name
  }

  return (
    <>
      {label && (
        <Text textStyle="l-xs" color="solid.500" mb="4px">
          {label}
        </Text>
      )}
      <Box
        p="16px"
        m="0px 0px 16px 0"
        bgColor="solid.25"
        borderRadius="8px"
        width="100%"
        justifyContent="space-between"
      >
        <Box>
          <Flex justifyContent="space-between" w="100%">
            <Flex
              w="calc(100% - 60px)"
              cursor={isUrl ? 'pointer' : 'default'}
              onClick={() => {
                if (isUrl) window.open(file as string, '_blank')
              }}
            >
              <Box
                mr="20px"
                minW="35px"
                h="35px"
                bgImage={getIconPath(file)}
                bgSize="contain"
                bgRepeat="no-repeat"
                bgPosition="center"
              />

              <Box w="calc(100% - 60px)">
                <Text mb="4px" textStyle="l-s" fontWeight="600" color="shade.900" noOfLines={1} overflow="hidden">
                  {getFileName()}
                </Text>
                <Flex>
                  <Text mr="4px" textStyle="b-xs" fontWeight="400" color="solid.500">
                    {isUrl ? `Uploaded` : `0 of ${file?.size}`} •{' '}
                  </Text>
                  {isDone && (
                    <>
                      <Icon mr="4px" width="16px" height="16px" viewBox="0 0 24 24" color="success.100">
                        <CheckCircleFilledIcon />
                      </Icon>
                      <Center>
                        <Text mr="4px" textStyle="b-xs" fontWeight="400" color="solid.500">
                          Completed
                        </Text>
                      </Center>
                    </>
                  )}
                  {isLoading && (
                    <>
                      <Icon mr="4px" width="16px" height="16px" viewBox="0 0 24 24" color="solid.500">
                        <Loading02Icon />
                      </Icon>
                      <Center>
                        <Text mr="4px" textStyle="b-xs" fontWeight="400" color="solid.500">
                          Uploading...
                        </Text>
                      </Center>
                    </>
                  )}
                  {!isDone && !isLoading && (
                    <Center>
                      <Text mr="4px" textStyle="b-xs" fontWeight="400" color="solid.500">
                        Ready
                      </Text>
                    </Center>
                  )}
                </Flex>
              </Box>
            </Flex>
            {onRemove !== undefined && (
              <IconButton
                isDisabled={isDisabled || isLoading}
                onClick={() => onRemove()}
                variant="unstyled"
                borderRadius="8px"
                aria-label="Delete file"
                border="none"
                _focus={{
                  boxShadow: 'none',
                  border: 'none',
                  outline: 'none',
                }}
                icon={
                  <Icon w="20px" h="20px" viewBox="0 0 24 24" fill="none" color="error.100">
                    <Trash04Icon />
                  </Icon>
                }
              />
            )}
          </Flex>
          {isLoading && (
            <Progress
              mt="16px"
              h="8px"
              isIndeterminate
              bgColor="transparent"
              borderRadius="full"
              value={80}
              colorScheme="alpha"
            />
          )}
        </Box>
        {extra && <Divider borderColor="neutral.100" my="12px" />}
        {extra && extra}
      </Box>
    </>
  )
}

export default UploadedFile
